import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  bigUnitNameOf,
  fail,
  handle,
  idOf,
  materialOf,
  nextDocNo,
  parse,
  requireRole,
  roleCodesOf,
  round,
  statusCodeOf,
  txLog,
  workflowRunner
} from './c07.shared'
import { nextUnitCode, recommendRackBin } from './inboundRmOperator.handler'
import { notify, userName, wmsRoute } from './notification.shared'

/**
 * Endpoint custom C07 Retur Sisa WIP (WF-WIP-01, `wip_return`) dan Retur Tembakau (WF-TOB-02, `rm_tobacco_return`) — WIP-01:
 * - `GET wip-return/issued` (SPK yang materialnya pernah di-issue + sisa yang boleh dikembalikan),
 *   `POST wip-return/create` (Produksi), `POST wip-return/accept` (Admin: Good/NG per baris + operator putaway →
 *   unit baru + QR Box Good/NG + tugas putaway IRM-07 + tiket NG).
 * - `GET tobacco-return/limits`, `POST tobacco-return/create` (Produksi), `accept` (stiker per baris),
 *   `assign-putaway` (bagi putaway ke satu/lebih operator, G10).
 * Tolak + alasan lewat `/api/workflow/submission/v2`. Penutupan "In Storage": `closeReturnsAfterPutaway` (c07.shared).
 */

const wipRunner = workflowRunner('WF-WIP-01', 'wip_return')
const tobRunner = workflowRunner('WF-TOB-02', 'rm_tobacco_return')
const NG_ZONE = 'QN-NG'

const zoneByCode = (code: string) => readTable('warehouse_zone').find(zone => String(zone.code) === code)
const zoneOfBin = (bin: LocalRow | null) => {
  if (!bin) return undefined
  const rack = findRow('mt_rack', idOf(bin.rack_id))
  return findRow('warehouse_zone', idOf(rack?.zone_id))
}

function requireOperator(operatorId: unknown) {
  const id = String(operatorId ?? '')
  if (!id || !roleCodesOf(id).includes('operator_wh_rm')) fail('Pilih operator untuk putaway.')
  return findRow('directus_users', id) as LocalRow
}

function createPutawayTask(
  operatorId: string,
  entries: { unit: LocalRow; qty: number; ng: boolean }[],
  source: { label: string; docNo: string }
) {
  const task = insertRow('rm_putaway_task', {
    ...auditFieldsForCreate(),
    id: newId(),
    task_no: nextDocNo('PWR', 'rm_putaway_task', 'task_no'),
    operator_id: operatorId,
    task_status: 'ASSIGNED',
    phase: 'PICK',
    note: null
  })
  entries.forEach(({ unit, qty, ng }) => {
    const bin = ng ? null : recommendRackBin(idOf(unit.material_id))
    insertRow('rm_putaway_task_item', {
      ...auditFieldsForCreate(),
      id: newId(),
      task_id: task.id,
      unit_id: unit.id,
      recommended_rack_bin_id: bin?.id ?? null,
      recommended_warehouse_zone_id: ng ? (zoneByCode(NG_ZONE)?.id ?? null) : (zoneOfBin(bin)?.id ?? null),
      actual_rack_bin_id: null,
      actual_warehouse_zone_id: null,
      qty_assigned: qty,
      qty_placed: 0,
      qty_short: null,
      picked_at: null,
      override_reason: null
    })
  })
  /* Operator diberi tahu setiap tugas putaway retur terbit (WIP & tembakau) — G10: satu notifikasi per tugas. */
  notify({
    to: { userIds: [operatorId] },
    type: 'PUTAWAY_ASSIGNED',
    title: `Tugas putaway ${String(task.task_no)} untuk Anda`,
    body: `${userName(currentUserId())} menugaskan putaway ${source.label} ${source.docNo} (${entries.length} unit) — buka Putaway RM — Operator.`,
    link: wmsRoute('inbound-rm/putaway-rm-operator', { task: String(task.id) }),
    ref: { type: 'rm_putaway_task', id: String(task.id) },
    dedupeKey: `PUTAWAY_ASSIGNED:${String(task.id)}`
  })
  return task
}

const dayCode = (date = new Date()) =>
  `${String(date.getFullYear()).slice(2)}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`

function newUnit(material: LocalRow, qty: number, flag: 'REGULAR' | 'NG', reserved: string[]) {
  const perBig = Number(material.default_purchase_unit_qty ?? 1)
  const batchNo = `${material.code}-${dayCode()}-90`
  const qr = nextUnitCode(batchNo, reserved)
  reserved.push(qr)
  return insertRow('rm_unit', {
    ...auditFieldsForCreate(),
    id: newId(),
    uid: `UNIT-${String(readTable('rm_unit').length + 1).padStart(6, '0')}`,
    qr_code: qr,
    material_id: material.id,
    batch_no: batchNo,
    supplier_id: null,
    po_no: null,
    qty_small: qty * perBig,
    rack_bin_id: null,
    warehouse_zone_id: null,
    flag,
    unit_status: flag === 'NG' ? 'NG_AWAITING_PLACEMENT' : 'AWAITING_PUTAWAY',
    qc_date: new Date().toISOString(),
    unit_big_snapshot: bigUnitNameOf(material),
    small_per_big_snapshot: perBig,
    is_leftover_wip: true,
    stored_at: null
  })
}

/* ------------------------------------------------------------------ WIP */

const wipLinesOf = (wip: LocalRow) => readTable('wip_return_line').filter(line => idOf(line.wip_return_id) === wip.id)

/** SPK yang materialnya pernah di-issue (qty diserahterimakan) dan sisa yang boleh dikembalikan per material. */
export function issuedMaterials() {
  const returned = new Map<string, number>()
  readTable('wip_return')
    .filter(row => statusCodeOf(row) !== 'WIP_REJECTED')
    .forEach(row =>
      wipLinesOf(row).forEach(line => {
        const key = `${row.spk_no}|${idOf(line.material_id)}`
        returned.set(key, round((returned.get(key) ?? 0) + Number(line.qty_reported ?? 0)))
      })
    )
  const spks = readTable('material_request')
    .filter(request => request.type === 'SPK' && request.spk_no)
    .map(request => {
      const delivered = new Map<string, number>()
      readTable('material_request_line')
        .filter(line => idOf(line.request_id) === request.id)
        .forEach(line =>
          delivered.set(
            idOf(line.material_id),
            round((delivered.get(idOf(line.material_id)) ?? 0) + Number(line.settled_qty ?? 0))
          )
        )
      const lines = [...delivered.entries()]
        .map(([material_id, issued]) => {
          const material = materialOf(material_id) as LocalRow
          return {
            material_id,
            material_code: String(material.code),
            material_name: String(material.name),
            unit_name: bigUnitNameOf(material),
            issued,
            max: round(issued - (returned.get(`${request.spk_no}|${material_id}`) ?? 0))
          }
        })
        .filter(line => line.issued > 0 && line.max > 0)
      return {
        spk_no: String(request.spk_no),
        request_id: String(request.id),
        req_no: String(request.req_no),
        sku_name: String(findRow('mt_product', idOf(request.sku_id))?.name ?? ''),
        lines
      }
    })
    .filter(spk => spk.lines.length)
  return { spks }
}

type WipCreateBody = { spk_no?: string; note?: string; lines?: { material_id: string; qty: number; note?: string }[] }

export function createWipReturn(body: WipCreateBody) {
  requireRole('Retur WIP dibuat oleh Tim Produksi.', 'tim_produksi')
  const spk = issuedMaterials().spks.find(row => row.spk_no === String(body.spk_no ?? ''))
  if (!body.spk_no) fail('Pilih nomor SPK/WO dulu.')
  if (!spk) fail('SPK ini belum punya material yang di-issue ke produksi.')
  const lines = (body.lines ?? []).filter(line => line.material_id && Number(line.qty) > 0)
  if (!lines.length) fail('Isi minimal satu baris material yang dikembalikan.')
  lines.forEach((line, index) => {
    const limit = spk.lines.find(row => row.material_id === line.material_id)
    if (!limit) fail(`Material baris ${index + 1} bukan material SPK ini.`)
    if (!Number.isInteger(Number(line.qty)) || Number(line.qty) > limit.max)
      fail(`Jumlah item ${index + 1} harus kemasan penuh antara 1 dan ${limit.max}.`)
  })
  const id = newId()
  const { statusId, submissionId } = wipRunner.start(id)
  const wip = insertRow('wip_return', {
    ...auditFieldsForCreate(),
    id,
    rwip_no: nextDocNo('RWIP', 'wip_return', 'rwip_no'),
    spk_ref: spk.spk_no,
    spk_no: spk.spk_no,
    request_id: spk.request_id,
    putaway_operator_id: null,
    status_id: statusId,
    submission_id: submissionId,
    note: String(body.note ?? '').trim() || null
  })
  lines.forEach(line =>
    insertRow('wip_return_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      wip_return_id: id,
      material_id: line.material_id,
      unit_id: materialOf(line.material_id)?.default_purchase_unit_id ?? null,
      qty_reported: Number(line.qty),
      qty_good: null,
      qty_ng: null,
      ng_reason_id: null,
      ng_reason: null,
      note: String(line.note ?? '').trim() || null,
      good_unit_id: null,
      ng_unit_id: null
    })
  )
  wipRunner.run(wip, 'ACT_WIP_SEND', ['ACT_WIP_PRODUKSI'])
  txLog(
    'WIP Return',
    'WIP-01',
    'wip-dikirim',
    String(wip.rwip_no),
    `Retur WIP ${wip.rwip_no} dari SPK ${spk.spk_no} — ${lines.length} item`
  )
  return { id, rwip_no: wip.rwip_no }
}

type WipAcceptBody = {
  wip_id?: string
  operator_id?: string
  lines?: { line_id: string; good: number; ng: number; ng_reason?: string }[]
}

export function acceptWipReturn(body: WipAcceptBody) {
  requireRole('Retur WIP diputuskan oleh Admin WH RM.', 'admin_wh_rm')
  const wip = findRow('wip_return', String(body.wip_id ?? ''))
  if (!wip) fail('Retur WIP tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (statusCodeOf(wip as LocalRow) !== 'WIP_PENDING_CHECK')
    fail('Status retur tidak sesuai untuk aksi ini.', 'INVALID_FROM_STATUS', 409)
  const lines = wipLinesOf(wip as LocalRow)
  const inputs = lines.map((line, index) => {
    const entry = (body.lines ?? []).find(row => row.line_id === line.id)
    const good = Number(entry?.good ?? 0)
    const ng = Number(entry?.ng ?? 0)
    if (!Number.isInteger(good) || !Number.isInteger(ng) || good < 0 || ng < 0 || good + ng <= 0)
      fail(`Isi jumlah Good atau NG untuk item ${index + 1}.`)
    const reason = String(entry?.ng_reason ?? '').trim()
    if (ng > 0 && !reason) fail(`Alasan NG wajib diisi (item ${index + 1}).`)
    return { line, good, ng, reason }
  })
  const operator = requireOperator(body.operator_id)
  const reserved: string[] = []
  const putaway: { unit: LocalRow; qty: number; ng: boolean }[] = []
  const labels: Record<string, unknown>[] = []
  inputs.forEach(({ line, good, ng, reason }) => {
    const material = materialOf(line.material_id) as LocalRow
    const goodUnit = good > 0 ? newUnit(material, good, 'REGULAR', reserved) : null
    const ngUnit = ng > 0 ? newUnit(material, ng, 'NG', reserved) : null
    if (goodUnit) putaway.push({ unit: goodUnit, qty: good, ng: false })
    if (ngUnit) {
      putaway.push({ unit: ngUnit, qty: ng, ng: true })
      insertRow('ng_ticket', {
        ...auditFieldsForCreate(),
        id: newId(),
        ticket_no: nextDocNo('NGT', 'ng_ticket', 'ticket_no'),
        unit_id: ngUnit.id,
        supplier_id: null,
        qty: ng,
        parameter_result: `Retur WIP ${wip.rwip_no} — ${reason}`,
        placement_rack_bin_id: null,
        placement_warehouse_zone_id: null,
        ticket_status: 'OPEN'
      })
    }
    updateRow('wip_return_line', line.id, {
      qty_good: good,
      qty_ng: ng,
      ng_reason: ng > 0 ? reason : null,
      good_unit_id: goodUnit?.id ?? null,
      ng_unit_id: ngUnit?.id ?? null,
      ...auditFieldsForUpdate()
    })
    ;[goodUnit, ngUnit].forEach(unit => {
      if (!unit) return
      labels.push({
        unit_qr: unit.qr_code,
        flag: unit.flag,
        qty: unit.flag === 'NG' ? ng : good,
        unit: bigUnitNameOf(material),
        material_code: material.code,
        material_name: material.name,
        batch_no: unit.batch_no
      })
    })
    const diff = Number(line.qty_reported) - good - ng
    if (diff !== 0)
      txLog(
        'WIP Return',
        'WIP-01',
        'wip-selisih',
        String(wip.rwip_no),
        `${material.code}: dilaporkan ${line.qty_reported}, Good ${good} + NG ${ng} (selisih ${diff > 0 ? '−' : '+'}${Math.abs(diff)})`
      )
  })
  const task = createPutawayTask(String(operator.id), putaway, { label: 'retur sisa WIP', docNo: String(wip.rwip_no) })
  updateRow('wip_return', wip.id, { putaway_operator_id: operator.id, ...auditFieldsForUpdate() })
  wipRunner.run(wip as LocalRow, 'ACT_WIP_CHECK_OK', ['ACT_WIP_ADMIN_RM'])
  txLog(
    'WIP Return',
    'WIP-01',
    'wip-diterima',
    String(wip.rwip_no),
    `Retur diterima — ${putaway.length} unit baru, tugas putaway ${task.task_no} untuk ${operator.first_name}`
  )
  return { id: wip.id, task_no: task.task_no, labels }
}

/* -------------------------------------------------------------- tembakau */

const tobLinesOf = (row: LocalRow) =>
  readTable('rm_tobacco_return_line').filter(line => idOf(line.return_id) === row.id)

/** Batas kembali per mesin × jenis = tembakau yang diserahterimakan ke mesin − yang sudah dikembalikan. */
export function tobaccoLimits() {
  const issued = new Map<string, number>()
  readTable('material_request')
    .filter(request => request.type === 'TOBACCO')
    .forEach(request =>
      readTable('material_request_line')
        .filter(line => idOf(line.request_id) === request.id)
        .forEach(line => {
          const key = `${idOf(line.machine_id)}|${idOf(line.material_id)}`
          issued.set(key, round((issued.get(key) ?? 0) + Number(line.settled_qty ?? 0)))
        })
    )
  readTable('rm_tobacco_return')
    .filter(row => statusCodeOf(row) !== 'TOB_RTN_REJECTED')
    .forEach(row =>
      tobLinesOf(row).forEach(line => {
        const key = `${idOf(line.machine_id)}|${idOf(line.material_id)}`
        issued.set(key, round((issued.get(key) ?? 0) - Number(line.qty_sak ?? 0)))
      })
    )
  return {
    items: [...issued.entries()]
      .map(([key, max]) => {
        const [machine_id, material_id] = key.split('|')
        return { machine_id, material_id, max }
      })
      .filter(item => item.max > 0)
  }
}

type TobCreateBody = { note?: string; lines?: { machine_id: string; material_id: string; qty_sak: number }[] }

export function createTobaccoReturn(body: TobCreateBody) {
  requireRole('Retur tembakau dibuat oleh Tim Produksi.', 'tim_produksi')
  const rows = body.lines ?? []
  if (!rows.length) fail('Isi minimal satu baris.')
  if (rows.some(line => !line.material_id)) fail('Masih ada baris tanpa jenis tembakau.')
  if (rows.some(line => !line.machine_id)) fail('Pilih mesin asal untuk setiap baris.')
  if (rows.some(line => !(Number(line.qty_sak) > 0))) fail('Jumlah Sak harus lebih dari nol.')
  const tobaccoGroup = readTable('mt_material_group').find(group => group.code === 'TEMBAKAU')
  if (!readTable('mt_rack').some(rack => idOf(rack.category_restriction_id) === tobaccoGroup?.id))
    fail('Belum ada rak yang boleh menampung Tembakau di Master Lokasi.')
  const limits = tobaccoLimits().items
  const merged = new Map<string, { machine_id: string; material_id: string; qty: number }>()
  rows.forEach(line => {
    const key = `${line.machine_id}|${line.material_id}`
    const existing = merged.get(key)
    if (existing) existing.qty += Number(line.qty_sak)
    else merged.set(key, { machine_id: line.machine_id, material_id: line.material_id, qty: Number(line.qty_sak) })
  })
  merged.forEach(line => {
    const max =
      limits.find(item => item.machine_id === line.machine_id && item.material_id === line.material_id)?.max ?? 0
    if (!Number.isInteger(line.qty) || line.qty < 1 || line.qty > max) fail(`Jumlahnya harus antara 1 dan ${max}.`)
  })
  const [first] = [...merged.values()]
  const id = newId()
  const { statusId, submissionId } = tobRunner.start(id)
  const row = insertRow('rm_tobacco_return', {
    ...auditFieldsForCreate(),
    id,
    return_no: nextDocNo('TBR', 'rm_tobacco_return', 'return_no'),
    machine_id: first.machine_id,
    status_id: statusId,
    submission_id: submissionId,
    note: String(body.note ?? '').trim() || null
  })
  merged.forEach(line =>
    insertRow('rm_tobacco_return_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      return_id: id,
      machine_id: line.machine_id,
      material_id: line.material_id,
      qty_sak: line.qty,
      unit_id: null,
      note: null
    })
  )
  tobRunner.run(row, 'ACT_TOB_RTN_SEND', ['ACT_TOB_RTN_PRODUKSI'])
  txLog(
    'WIP Return',
    'WIP-01',
    'tembakau-diajukan',
    String(row.return_no),
    `Pengajuan retur tembakau ${row.return_no} — ${merged.size} baris`
  )
  return { id, return_no: row.return_no }
}

export function acceptTobaccoReturn(body: { return_id?: string }) {
  requireRole('Retur tembakau diputuskan oleh Admin WH RM.', 'admin_wh_rm')
  const row = findRow('rm_tobacco_return', String(body.return_id ?? ''))
  if (!row) fail('Pengajuan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (statusCodeOf(row as LocalRow) !== 'TOB_RTN_PENDING_CHECK')
    fail('Status pengajuan tidak sesuai untuk aksi ini.', 'INVALID_FROM_STATUS', 409)
  const reserved: string[] = []
  const stickers = tobLinesOf(row as LocalRow).map(line => {
    const material = materialOf(line.material_id) as LocalRow
    const unit = newUnit(material, Number(line.qty_sak), 'REGULAR', reserved)
    updateRow('rm_tobacco_return_line', line.id, { unit_id: unit.id, ...auditFieldsForUpdate() })
    return {
      line_id: line.id,
      unit_qr: unit.qr_code,
      material_code: material.code,
      qty: Number(line.qty_sak),
      batch_no: unit.batch_no
    }
  })
  tobRunner.run(row as LocalRow, 'ACT_TOB_RTN_VERIFY', ['ACT_TOB_RTN_ADMIN_RM'])
  txLog(
    'WIP Return',
    'WIP-01',
    'tembakau-diterima',
    String(row.return_no),
    `Retur tembakau diterima — ${stickers.length} stiker terbit`
  )
  return { id: row.id, stickers }
}

type TobAssignBody = { return_id?: string; split?: { operator_id: string; count: number }[] }

export function assignTobaccoPutaway(body: TobAssignBody) {
  requireRole('Putaway retur tembakau dibagi oleh Admin WH RM.', 'admin_wh_rm')
  const row = findRow('rm_tobacco_return', String(body.return_id ?? ''))
  if (!row) fail('Pengajuan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (statusCodeOf(row as LocalRow) !== 'TOB_RTN_CHECKED') fail('Terima pengajuan dulu sebelum menugaskan putaway.')
  const assigned = new Set(readTable('rm_putaway_task_item').map(item => idOf(item.unit_id)))
  const pending = tobLinesOf(row as LocalRow).filter(line => line.unit_id && !assigned.has(idOf(line.unit_id)))
  if (!pending.length) fail('Semua stiker sudah punya tugas putaway.')
  const split = (body.split ?? []).filter(part => Number(part.count) > 0)
  if (!split.length || split.reduce((sum, part) => sum + Number(part.count), 0) !== pending.length)
    fail(`Jumlah stiker yang dibagi harus ${pending.length}.`)
  let cursor = 0
  const tasks = split.map(part => {
    const operator = requireOperator(part.operator_id)
    const entries = pending.slice(cursor, cursor + Number(part.count)).map(line => ({
      unit: findRow('rm_unit', idOf(line.unit_id)) as LocalRow,
      qty: Number(line.qty_sak),
      ng: false
    }))
    cursor += Number(part.count)
    const task = createPutawayTask(String(operator.id), entries, {
      label: 'retur tembakau',
      docNo: String(row.return_no)
    })
    return `${task.task_no} (${operator.first_name})`
  })
  txLog('WIP Return', 'WIP-01', 'tembakau-putaway', String(row.return_no), `Putaway dibagi: ${tasks.join(', ')}`)
  return { tasks }
}

/* ------------------------------------------------------------- registrasi */

export function registerWipReturnHandlers(mock: MockAdapter) {
  const wip = '/api/v1/wms/wip-return'
  const tob = '/api/v1/wms/tobacco-return'
  mock.onGet(`${wip}/issued`).reply(() => handle(() => issuedMaterials()))
  mock.onPost(`${wip}/create`).reply(config => handle(() => createWipReturn(parse(config.data))))
  mock.onPost(`${wip}/accept`).reply(config => handle(() => acceptWipReturn(parse(config.data))))
  mock.onGet(`${tob}/limits`).reply(() => handle(() => tobaccoLimits()))
  mock.onPost(`${tob}/create`).reply(config => handle(() => createTobaccoReturn(parse(config.data))))
  mock.onPost(`${tob}/accept`).reply(config => handle(() => acceptTobaccoReturn(parse(config.data))))
  mock.onPost(`${tob}/assign-putaway`).reply(config => handle(() => assignTobaccoPutaway(parse(config.data))))
}
